import { useTranslation } from 'react-i18next'
import { useSnackbar } from 'notistack'
import { Controller, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { Stack } from '@mui/material'
import { LoadingButton } from '@mui/lab'
import { ModalLayout } from '@/components'
import {
  ControlledDateField,
  SearchAutocomplete,
  SearchAutocompleteOption,
} from '@/components/inputs'
import { useHrInternalHoursExport, useUsersByGroups } from '@/hooks'
import * as yup from 'yup'
import dayjs from 'dayjs'
import { DATE_FORMAT_YMD } from '@/constants'
import { UserGroups } from '@/types'

type HrInternalHoursExportFormData = {
  startDate: string | null
  endDate: string | null
  users: string | null
}

const exportSchema = yup.object({
  startDate: yup.date().typeError('invalid_date').required(),
  endDate: yup.date().typeError('invalid_date').required(),
  users: yup.string().optional().nullable(),
})

const defaultValues: HrInternalHoursExportFormData = {
  startDate: null,
  endDate: null,
  users: null,
}

export const HrInternalHoursExportForm = (): JSX.Element => {
  const { t } = useTranslation('hrInternalHours')
  const { enqueueSnackbar } = useSnackbar()
  const { exportInternalHours, loading } = useHrInternalHoursExport()
  const { data: internalEmployees } = useUsersByGroups({
    groups: [UserGroups.INTERNAL_EMPLOYEE],
  })

  const {
    control,
    handleSubmit,
    reset,
    formState: { isValid },
  } = useForm<HrInternalHoursExportFormData>({
    mode: 'onChange',
    defaultValues,
    resolver: yupResolver(exportSchema),
  })

  const handleExportSubmit = async ({
    startDate,
    endDate,
    users,
  }: HrInternalHoursExportFormData) => {
    try {
      await exportInternalHours({
        startDate: startDate
          ? dayjs(startDate).format(DATE_FORMAT_YMD)
          : undefined,
        endDate: endDate ? dayjs(endDate).format(DATE_FORMAT_YMD) : undefined,
        users: users || undefined,
      })

      enqueueSnackbar(t('export.success'), { variant: 'success' })
      reset(defaultValues)
    } catch (e) {
      enqueueSnackbar(`${t('errors:api.execute')}: ${e}`, { variant: 'error' })
    }
  }

  return (
    <ModalLayout title={t('export.title')}>
      <form onSubmit={handleSubmit(handleExportSubmit)}>
        <Stack spacing={3}>
          <Stack direction="row" spacing={2}>
            <ControlledDateField
              name="startDate"
              placeholder={t('forms:placeholders.start-date')}
              control={control}
              apiFieldName="startDate"
            />
            <ControlledDateField
              name="endDate"
              placeholder={t('forms:placeholders.end-date')}
              control={control}
              apiFieldName="endDate"
            />
          </Stack>

          <Controller
            name="users"
            control={control}
            render={({ field }) => (
              <SearchAutocomplete
                value={field.value as string}
                onChange={field.onChange}
                options={internalEmployees?.map(
                  ({ id, first_name, last_name, email }) =>
                    ({
                      label: first_name
                        ? `${id}: ${first_name} ${last_name}`
                        : `${id}: ${email}`,
                      value: id.toString(),
                    } as SearchAutocompleteOption)
                )}
                sort={true}
                placeholder={t('export.users')}
              />
            )}
          />

          <Stack direction="row" justifyContent="flex-end">
            <LoadingButton
              variant="contained"
              color="secondary"
              type="submit"
              loading={loading}
              disabled={!isValid}
              disableElevation={true}
            >
              <span>{t('export.button')}</span>
            </LoadingButton>
          </Stack>
        </Stack>
      </form>
    </ModalLayout>
  )
}
